import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ordersAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Zap, ArrowLeft, Package, Clock } from 'lucide-react';

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await ordersAPI.getAll();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/30';
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              LuxLyx
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Shop
        </Button>

        <h1 className="text-4xl font-bold mb-8">My Orders</h1>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20">
            <Package className="h-20 w-20 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-xl mb-6">No orders yet</p>
            <Button
              data-testid="shop-now-btn"
              onClick={() => navigate('/products')}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div
                key={order.id}
                data-testid={`order-${order.id}`}
                className="bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Order ID</div>
                    <div className="font-bold text-lg">{order.id}</div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className={`px-4 py-2 rounded-full border font-semibold ${getStatusColor(order.status)}`}>
                      {order.status.toUpperCase()}
                    </div>
                    <div className={`px-4 py-2 rounded-full border font-semibold ${
                      order.payment_status === 'paid'
                        ? 'text-green-400 bg-green-400/10 border-green-400/30'
                        : 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30'
                    }`}>
                      {order.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Shipping Address</div>
                    <div className="text-gray-300">
                      {order.shipping_address.name}<br />
                      {order.shipping_address.address}<br />
                      {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}<br />
                      {order.shipping_address.country}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Order Date</div>
                    <div className="text-gray-300 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <div className="text-sm text-gray-400 mb-3">Items</div>
                  <div className="space-y-3">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="text-gray-300">
                          {item.name} <span className="text-gray-500">x{item.quantity}</span>
                        </div>
                        <div className="font-semibold">${(item.price * item.quantity).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/10 mt-4 pt-4 flex justify-between items-center">
                    <div className="text-lg font-bold">Total</div>
                    <div className="text-2xl font-bold text-yellow-400">${order.total_amount.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
